import React, { useState, useEffect } from "react";
import { Landmark, BarChart3, Clock, Search, Loader2, Sparkles, TrendingUp, Trophy, Droplets, Percent, Home, LineChart as LineIcon } from "lucide-react";
import { getAreaIntel, getDealIntel, getTimeline } from "../../api";
import { useToast } from "../../hooks/use-toast";

const fmt = (n) => (n || n === 0 ? `\u00A3${Number(n).toLocaleString()}` : "\u2014");

const AiNote = ({ text }) => {
  if (!text) return null;
  const rec = text.split(/recommendation:/i);
  const body = rec[0].trim();
  const action = rec[1]?.trim();
  const clean = (s) => (s || "").replace(/\*\*/g, "");
  return (
    <div className="mt-5 rounded-xl bg-gradient-to-r from-[#1c1c1e] to-[#2b2b2b] p-4 flex items-start gap-3" data-testid="intel-ai-note">
      <span className="w-8 h-8 rounded-lg bg-[#b8935f]/20 flex items-center justify-center shrink-0"><Sparkles size={16} className="text-[#d9b98a]" /></span>
      <div className="min-w-0">
        <p className="text-[11px] uppercase tracking-[0.14em] text-[#d9b98a] mb-1">AI market read</p>
        <p className="text-[13px] text-white/85 leading-relaxed">{clean(body)}</p>
        {action && <p className="text-[13px] text-[#e8d4b8] mt-2"><span className="font-600">Recommendation:</span> {clean(action)}</p>}
      </div>
    </div>
  );
};

const LineChart = ({ data }) => {
  if (!data || data.length < 2) return null;
  const w = 520, h = 150, pad = 30;
  const vals = data.map((d) => d.value);
  const min = Math.min(...vals), max = Math.max(...vals);
  const span = max - min || 1;
  const x = (i) => pad + (i * (w - pad * 2)) / (data.length - 1);
  const y = (v) => h - pad - ((v - min) / span) * (h - pad * 2);
  const path = data.map((d, i) => `${i === 0 ? "M" : "L"}${x(i)},${y(d.value)}`).join(" ");
  const area = `${path} L${x(data.length - 1)},${h - pad} L${x(0)},${h - pad} Z`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full" data-testid="intel-price-chart">
      <defs><linearGradient id="ig" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#b8935f" stopOpacity="0.28" /><stop offset="100%" stopColor="#b8935f" stopOpacity="0" /></linearGradient></defs>
      <path d={area} fill="url(#ig)" />
      <path d={path} fill="none" stroke="#b8935f" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      {data.map((d, i) => (
        <g key={i}>
          <circle cx={x(i)} cy={y(d.value)} r="3.2" fill="#b8935f" />
          <text x={x(i)} y={h - 8} textAnchor="middle" className="fill-[#a0a0a0]" style={{ fontSize: 10 }}>{d.label}</text>
        </g>
      ))}
    </svg>
  );
};

const BarChart = ({ data }) => {
  if (!data || !data.length) return null;
  const max = Math.max(...data.map((d) => d.value), 1);
  return (
    <div className="flex items-end gap-2 h-[130px]" data-testid="intel-distribution-chart">
      {data.map((d) => (
        <div key={d.label} className="flex-1 flex flex-col items-center justify-end gap-1.5">
          <span className="text-[11px] text-[#6a6a6a] font-500">{d.value}</span>
          <div className="w-full rounded-t-md bg-[#b8935f]/80 hover:bg-[#b8935f] transition-colors" style={{ height: `${Math.max(4, (d.value / max) * 90)}%` }} />
          <span className="text-[9px] text-[#a0a0a0] text-center leading-tight">{d.label}</span>
        </div>
      ))}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, tint }) => (
  <div className="bg-[#faf7f2] rounded-xl border border-[#f0ece5] p-4">
    <p className="text-[11px] text-[#a0a0a0] uppercase tracking-wide flex items-center gap-1.5">{Icon && <Icon size={12} className={tint || "text-[#b8935f]"} />}{label}</p>
    <p className="font-display text-[20px] font-500 text-[#2b2b2b] mt-1">{value}</p>
  </div>
);

const scoreColor = (s) => (s >= 80 ? "bg-green-500" : s >= 60 ? "bg-amber-500" : "bg-[#c9b18e]");

const GrowthIntelligence = () => {
  const { toast } = useToast();
  const [postcode, setPostcode] = useState("BA1");
  const [intel, setIntel] = useState(null);
  const [busy, setBusy] = useState(false);

  const [deals, setDeals] = useState(null);
  const [dealsBusy, setDealsBusy] = useState(true);

  const [selected, setSelected] = useState("");
  const [timeline, setTimeline] = useState([]);

  useEffect(() => { getDealIntel().then((d) => setDeals(d)).catch(() => {}).finally(() => setDealsBusy(false)); }, []);
  useEffect(() => { lookup(); /* initial */ }, []); // eslint-disable-line
  useEffect(() => { if (selected) getTimeline(selected).then(setTimeline).catch(() => setTimeline([])); }, [selected]);

  const lookup = async (e) => {
    if (e) e.preventDefault();
    if (!postcode.trim()) return;
    setBusy(true);
    try {
      const d = await getAreaIntel(postcode.trim());
      setIntel(d);
    } catch { toast({ title: "Lookup failed", description: "Try a valid UK postcode (e.g. BA1)." }); }
    finally { setBusy(false); }
  };

  const s = intel?.stats || {};

  return (
    <div className="space-y-6" data-testid="growth-intelligence">
      {/* TOOL 1 — Area Intelligence */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Landmark size={17} className="text-[#b8935f]" /> Area Intelligence</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Deep market read by postcode — sold-price trend, price mix, yield & flood risk, powered by live HM Land Registry & PropertyData, with an AI recommendation.</p>
        <form onSubmit={lookup} className="flex gap-2 mb-5 max-w-md">
          <input value={postcode} onChange={(e) => setPostcode(e.target.value)} placeholder="Enter postcode or town (e.g. BA1, Bath)" data-testid="intel-postcode" className="flex-1 h-[46px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]" />
          <button type="submit" disabled={busy} data-testid="intel-lookup-btn" className="inline-flex items-center gap-2 px-5 rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] transition-colors disabled:opacity-60">{busy ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />} Analyse</button>
        </form>

        {intel && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
              <Stat icon={Home} label="Avg price" value={fmt(s.avgPrice)} />
              <Stat icon={BarChart3} label="Median" value={fmt(s.median)} />
              <Stat icon={Home} label="Avg rent" value={s.avgRent ? `${fmt(s.avgRent)} pcm` : "\u2014"} />
              <Stat icon={Percent} label="Gross yield" value={s.grossYield ? `${s.grossYield}%` : "\u2014"} />
              <Stat icon={TrendingUp} label="Price trend" value={s.growth12m != null ? `${s.growth12m > 0 ? "+" : ""}${s.growth12m}%` : "\u2014"} tint={s.growth12m >= 0 ? "text-green-500" : "text-red-500"} />
              <Stat icon={Droplets} label="Flood risk" value={s.floodRisk || "\u2014"} />
            </div>

            <div className="grid lg:grid-cols-2 gap-5">
              <div className="bg-[#faf7f2] rounded-xl border border-[#f0ece5] p-4">
                <p className="text-[12px] tracking-[0.1em] uppercase text-[#b8935f] mb-3 flex items-center gap-1.5"><LineIcon size={13} /> Sold-price trend</p>
                {intel.priceHistory?.length >= 2 ? <LineChart data={intel.priceHistory} /> : <p className="text-[13px] text-[#a0a0a0] py-8 text-center">Not enough dated sales for a trend here.</p>}
              </div>
              <div className="bg-[#faf7f2] rounded-xl border border-[#f0ece5] p-4">
                <p className="text-[12px] tracking-[0.1em] uppercase text-[#b8935f] mb-3 flex items-center gap-1.5"><BarChart3 size={13} /> Price mix ({s.salesAnalysed} sales)</p>
                {intel.distribution?.some((d) => d.value) ? <BarChart data={intel.distribution} /> : <p className="text-[13px] text-[#a0a0a0] py-8 text-center">No recent sold records to break down.</p>}
              </div>
            </div>

            {intel.recent?.length > 0 && (
              <div className="mt-5">
                <p className="text-[12px] tracking-[0.1em] uppercase text-[#b8935f] mb-2 flex items-center gap-1.5"><BarChart3 size={13} /> Recent sales</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {intel.recent.slice(0, 8).map((t, i) => (
                    <div key={i} className="flex items-center justify-between p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5] text-[13px]">
                      <span className="text-[#2b2b2b] font-500 truncate pr-2">{t.address}</span>
                      <span className="text-[#6a6a6a] whitespace-nowrap">{fmt(t.price)}{t.date ? ` \u00B7 ${t.date}` : ""}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <AiNote text={intel.commentary} />
          </>
        )}
      </div>

      {/* TOOL 2 — Deal Radar */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Trophy size={17} className="text-[#b8935f]" /> Deal Radar</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Every live listing scored 0–100 for investment appeal with an estimated gross yield, ranked best-first, plus an AI focus note.</p>
        {dealsBusy ? <div className="py-10 text-center"><Loader2 size={20} className="animate-spin text-[#b8935f] mx-auto" /></div> : deals && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-5 max-w-md">
              <Stat icon={Home} label="Listings" value={deals.summary.count} />
              <Stat icon={Trophy} label="Avg score" value={`${deals.summary.avgScore}/100`} />
              <Stat icon={Percent} label="Avg yield" value={`${deals.summary.avgYield}%`} />
            </div>
            <div className="space-y-2" data-testid="deal-radar-list">
              {(deals.scores || []).slice(0, 8).map((d) => (
                <div key={d.id} className="flex items-center gap-4 p-3 rounded-lg bg-[#faf7f2] border border-[#f0ece5]">
                  {d.image && <img src={d.image} alt="" className="w-12 h-12 rounded-lg object-cover shrink-0" />}
                  <div className="min-w-0 flex-1">
                    <p className="text-[14px] font-500 text-[#2b2b2b] truncate">{d.title}</p>
                    <p className="text-[12px] text-[#8a8a8a]">{fmt(d.price)} · {d.yield}% yield</p>
                    <div className="h-1.5 rounded-full bg-[#eee7db] mt-1.5 overflow-hidden"><div className={`h-full rounded-full ${scoreColor(d.score)}`} style={{ width: `${d.score}%` }} /></div>
                  </div>
                  <span className="font-display text-[22px] font-500 text-[#2b2b2b] w-12 text-right">{d.score}</span>
                </div>
              ))}
            </div>
            <AiNote text={deals.commentary} />
          </>
        )}
      </div>

      {/* TOOL 3 — Property Timeline */}
      <div className="bg-white rounded-2xl border border-[#f0ece5] p-6">
        <h3 className="font-display text-[18px] font-500 text-[#2b2b2b] mb-1 flex items-center gap-2"><Clock size={17} className="text-[#b8935f]" /> Property Timeline</h3>
        <p className="text-[13px] text-[#8a8a8a] mb-4">Track the full activity history of any listing — from first launch to offer.</p>
        <select value={selected} onChange={(e) => setSelected(e.target.value)} data-testid="timeline-select" className="w-full max-w-md h-[46px] px-4 rounded-lg border border-[#e2d8c8] text-[14px] mb-5 outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]">
          <option value="">Select a property…</option>
          {(deals?.scores || []).map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
        </select>
        {selected && (
          <div data-testid="timeline-events">
            {timeline.length === 0 ? <p className="text-[14px] text-[#8a8a8a]">No timeline events for this property.</p> : (
              <div className="relative pl-6">
                {timeline.map((ev, i) => (
                  <div key={ev.id || i} className="relative pb-5 last:pb-0">
                    <span className="absolute -left-6 top-1 w-3 h-3 rounded-full bg-[#b8935f]" />
                    {i < timeline.length - 1 && <span className="absolute -left-[19px] top-3 w-[2px] h-full bg-[#e2d8c8]" />}
                    <p className="text-[14px] text-[#2b2b2b]">{ev.event}</p>
                    <p className="text-[12px] text-[#a0a0a0]">{new Date(ev.at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default GrowthIntelligence;
